/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;
// R2: shared with the Picker broker so a specialized selector's provider-hardcoded Entity resolves
// identically for the Read/Print display as it does for the Edit picker.
const _SPECIALIZED_SELECTOR_MAP = require('./_SpecializedSelectorMap.js');
// Managed code lists (SampleStatus, …): the Read/Print label comes from the same table the Edit picker
// reads, so a status curated by the customer is not shown as its bare code here.
const libVocabularySelectorMap = require('./_VocabularySelectorMap.js');
const libTestResultOptions = require('./_TestResultOptions.js');
const libTestResultsSummary = require('./_TestResultsSummary.js');
// Shared with the Picker broker so Read/Print ignores a static select's Entity exactly as Edit does.
const libStaticSelectEntity = require('./_StaticSelectEntity.js');
const libEntityOptionSource = require('./_EntityOptionSource.js');
// The SAME "what counts as unset" rule the EDIT picker applies — shared rather than re-derived so
// Read/Print cannot drift from Edit, the failure mode this provider family keeps hitting.
const libPickerEmptyValues = require('./_PickerEmptyValues.js');
// The SAME display-name resolution order the legacy Select2Util chain applies (dotted paths, the
// Name-less User table) — shared so flipping a forward onto this provider is a no-visual-change move.
const libEntityDisplayName = require('./_EntityDisplayName.js');
const libSharedEntityRead = require('./_SharedEntityRead.js');

/**
 * PickerDisplay — the non-interactive Read/Print display for InputType 'Picker' (and the legacy
 * InputTypes port-forwarded onto it). ONE provider serves BOTH themes: it resolves the field's stored
 * value to its display markup — a static `SelectOptions` label, or an entity's name (Entity path), with
 * an optional EntityTag badge — and paints it into the `PICKER-DISPLAY-FOR` node the Read/Print templates
 * render. Read and Print differ only in the TEMPLATE that styles that node (Read = a locked control;
 * Print = static PDF text), so no second provider is needed — mirrors how `EntityDisplay` serves both.
 *
 * This lets the Picker stack own its own read/print display, so the legacy `Option` / `EntityDisplay`
 * display providers can eventually be decommissioned (plan §9). Value resolution is kept output-identical
 * to the legacy providers so flipping the forwards is a no-visual-change move:
 *   - static: the same `SelectOptions`/`SelectOptionsPickList` lookup as `Select.handlePDFDisplay`;
 *   - entity: the entity NAME + optional EntityTag badge, matching `EntityDisplay(Multiple)`'s compound
 *     output — but resolved through pict-section-picker's `createEntityResolveValue` (NOT
 *     `Select2Util.getEntityAsync`), so this provider carries no Select2Util dependency at decommission.
 *
 * NOTE (plan §9 cleanup): the compound markup deliberately keeps the legacy `.select2-*-entity-selection`
 * / `.tag.select2-entity-tag` class names so bulma-print.scss styles it identically in Read + PDF today.
 * When select2 CSS is retired, rename those classes here AND in bulma-print.scss in lockstep (they are
 * plain strings this provider owns — there is no code dependency on Select2Util).
 */
class PickerDisplayProvider extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/** @param {string} pRawHTMLID @return {string} */
	getDisplayNodeID(pRawHTMLID)
	{
		return `#PICKER-DISPLAY-FOR-${pRawHTMLID}`;
	}

	/** @param {string} pRawHTMLID @param {number|string} pRowIndex @return {string} */
	getTabularDisplayNodeID(pRawHTMLID, pRowIndex)
	{
		return `#PICKER-DISPLAY-TABULAR-DATA-${pRawHTMLID}-${pRowIndex}`;
	}

	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		// Fire-and-forget: the entity path is async, and the base class hook is synchronous (same shape as
		// EntityDisplay's FIXME). The node is painted whenever the resolve settles.
		this._paint(pView, pInput, pValue, null).catch((pError) => this.pict.log.error(`PickerDisplay paint error: ${pError}`));
		// Read/Print paint test results here instead of through SampleTestAcceptance, so the summary is asked from here too.
		if (libTestResultOptions.TEST_RESULT_INPUT_TYPES.indexOf(pInput?.PictForm?.InputType) >= 0)
		{
			libTestResultsSummary.requestSummaryRefresh(this.pict, pView);
		}
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._paint(pView, pInput, pValue, pRowIndex).catch((pError) => this.pict.log.error(`PickerDisplay paint error: ${pError}`));
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Resolve the value → display markup and paint it into the Read/Print display node (no-op when the node
	 * is absent, e.g. the Edit theme).
	 * @param {object} pView @param {object} pInput @param {any} pValue @param {number|string|null} pRowIndex
	 * @return {Promise<boolean>}
	 */
	async _paint(pView, pInput, pValue, pRowIndex)
	{
		const tmpNodeID = pRowIndex != null ?
			this.getTabularDisplayNodeID(pInput.Macro.RawHTMLID, pRowIndex) :
			this.getDisplayNodeID(pInput.Macro.RawHTMLID);
		const tmpElements = this.pict.ContentAssignment.getElement(tmpNodeID);
		if (!tmpElements || tmpElements.length < 1)
		{
			return false;
		}
		const tmpHTML = await this._buildDisplayHTML(pInput, pValue, pView, pRowIndex);
		this.pict.ContentAssignment.assignContent(tmpElements[0], tmpHTML);
		return true;
	}

	/** Whether this field holds multiple values — the legacy multi InputTypes, or an explicit `Multiple`. */
	_isMulti(pPictForm)
	{
		return !!(pPictForm.Multiple || pPictForm.InputType === 'Select2Multiple' || pPictForm.InputType === 'EntitySelectorMultiple');
	}

	/** Split the stored value into parts (multi = CSV). Unset → []. */
	_valueParts(pPictForm, pValue)
	{
		if (pValue === null || pValue === undefined || pValue === '')
		{
			return [];
		}
		if (this._isMulti(pPictForm))
		{
			return String(pValue).split(',').map((pPart) => pPart.trim()).filter((pPart) => pPart !== '');
		}
		return [ pValue ];
	}

	/**
	 * The field's PictForm, augmented for a specialized selector (LabSelector, …) whose Entity is
	 * provider-hardcoded rather than descriptor-carried: inject the inferred Entity + display field from
	 * the shared map so the entity-resolve path fires exactly as it would for a descriptor that carries
	 * Entity. A descriptor naming the map's own Entity still takes the map's display config (legacy ignored
	 * the descriptor's Entity); a non-specialized InputType, or one naming a different Entity, is untouched.
	 * @param {object} pInput @return {Record<string, any>}
	 */
	_resolvedPictForm(pInput)
	{
		libVocabularySelectorMap.applyVocabularySource(pInput);
		// Static result codes: the acceptance input stamps these in its own initialize hooks, which never
		// run in Read/Print, so without this the display prints the raw code (PEND) instead of Pending.
		libTestResultOptions.applyTestResultOptions(pInput);
		libStaticSelectEntity.dropStaticSelectEntity(pInput && pInput.PictForm);
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		const tmpSpecialized = _SPECIALIZED_SELECTOR_MAP[tmpPictForm.InputType];
		if (!tmpSpecialized || (tmpPictForm.Entity && tmpPictForm.Entity !== tmpSpecialized.Entity)) { return tmpPictForm; }
		return Object.assign({}, tmpPictForm,
		{
			Entity: tmpSpecialized.Entity,
			TextField: tmpPictForm.TextField || tmpPictForm.EntityDisplayField || tmpSpecialized.TextField,
			EntityTag: tmpPictForm.EntityTag || tmpSpecialized.EntityTag,
			RecordIdentity: tmpPictForm.RecordIdentity || tmpSpecialized.RecordIdentity,
			// A multi-by-default selector (Lab/LineItem) gets Multiple from its EDIT provider's init at
			// runtime — which never runs in Read/Print, so the map carries it (?? keeps an explicit false).
			Multiple: tmpPictForm.Multiple ?? tmpSpecialized.Multiple,
		});
	}

	/**
	 * The field the committed value identifies its record by — mirrors the broker/cascade identity
	 * precedence (a GUID/Hash-identity selector like Sample or Test carries its RecordIdentity through
	 * the shared map, so the display resolves the SAME way the Edit picker committed).
	 * @param {object} pPictForm @return {string}
	 */
	_identityField(pPictForm)
	{
		return pPictForm.RecordIdentity || pPictForm.IdentityField || `ID${pPictForm.Entity || ''}`;
	}

	/**
	 * The field's `__Record` address — the broker's address scheme replicated (an explicit
	 * PictForm.RecordAddress wins; tabular composes through Informary).
	 * @param {object} pInput @param {number|string|null} pRowIndex @return {string}
	 */
	_recordAddress(pInput, pRowIndex)
	{
		if (pInput.PictForm?.RecordAddress)
		{
			return pInput.PictForm.RecordAddress;
		}
		if (pRowIndex != null)
		{
			return this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress) + '__Record';
		}
		return `${pInput.DataAddress || pInput.Hash}__Record`;
	}

	/**
	 * The locally persisted `__Record` snapshot(s) for this field, always as an array (multi persists an
	 * array, single an object). Saved forms persist `__Record` beside the value, and a created-but-unsaved
	 * record exists ONLY here — so the display resolves from it first, before any fetch.
	 * @param {object} pView @param {object} pInput @param {number|string|null} pRowIndex
	 * @return {Array<Record<string, any>>}
	 */
	_localEntityRecords(pView, pInput, pRowIndex)
	{
		if (!pView || typeof pView.getMarshalDestinationObject !== 'function')
		{
			return [];
		}
		const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._recordAddress(pInput, pRowIndex));
		if (Array.isArray(tmpRecord))
		{
			return tmpRecord.filter((pEntry) => pEntry && typeof pEntry === 'object');
		}
		return (tmpRecord && typeof tmpRecord === 'object' && Object.keys(tmpRecord).length > 0) ? [ tmpRecord ] : [];
	}

	/**
	 *`{ name, tag }` straight off a full entity record (the `__Record` / identity-fetch paths): name from
	 * the display field, tag from EntityTag — including the legacy `<Field>:date` tag format.
	 * @param {Record<string, any>} pRecord @param {object} pPictForm @param {string} [pFieldLabel]
	 * @return {{name:string, tag:any}}
	 */
	_recordParts(pRecord, pPictForm, pFieldLabel)
	{
		// No fallback thunk: the TextField branch never calls one, so the blank case is labelled here instead.
		let tmpName = libEntityDisplayName.resolveEntityDisplayName(pPictForm.Entity, pRecord, pPictForm);
		if (tmpName === undefined || tmpName === null || String(tmpName).trim() === '')
		{
			tmpName = this._unnamedLabel(pPictForm, pRecord, undefined, pFieldLabel);
		}
		let tmpTag = null;
		if (pPictForm.EntityTag)
		{
			let tmpTagField = pPictForm.EntityTag;
			const tmpIsDate = tmpTagField.includes(':date');
			if (tmpIsDate)
			{
				tmpTagField = tmpTagField.replace(':date', '');
			}
			tmpTag = pRecord[tmpTagField];
			if (tmpIsDate && tmpTag && this.pict.Dates?.dayJS)
			{
				tmpTag = this.pict.Dates.dayJS(tmpTag).format('YYYY-MM-DD');
			}
			if (tmpTag === undefined || tmpTag === null || tmpTag === '')
			{
				tmpTag = null;
			}
		}
		return { name: (tmpName === undefined || tmpName === null) ? '' : String(tmpName), tag: tmpTag };
	}

	/**
	 * Build the display HTML for the stored value(s): plain comma-joined label(s) for static / untagged
	 * entity fields, or the compound `[tag] name` markup (comma-joined for multi) when an EntityTag is
	 * configured — output-identical to EntityDisplay(Multiple). Empty value → ''. pView/pRowIndex (when
	 * the caller has them) enable the `__Record`-first resolve.
	 * @param {object} pInput @param {any} pValue @param {object} [pView] @param {number|string|null} [pRowIndex]
	 * @return {Promise<string>}
	 */
	async _buildDisplayHTML(pInput, pValue, pView, pRowIndex)
	{
		const tmpPictForm = this._resolvedPictForm(pInput);
		const tmpParts = await this._resolveParts(pInput, pValue, pView, pRowIndex);
		if (tmpParts.length < 1)
		{
			return '';
		}
		// Compound tag+name only when an EntityTag is configured (entity fields); everything else is a
		// plain, comma-joined label list (the current, verified behavior).
		if (tmpPictForm.Entity && tmpPictForm.EntityTag)
		{
			const tmpMulti = this._isMulti(tmpPictForm);
			const tmpTagLast = !!tmpPictForm.EntityTagLast;
			// A configured-but-empty tag paints no badge at all — per part, so a mixed list still tags the
			// records that have one. The bare name is exactly what an untagged entity descriptor renders,
			// which also keeps the badge-compensating print padding on .select2-single-entity-selection off
			// a field with no badge to compensate for. Every tag path normalizes ''/undefined to null, so a
			// numeric 0 stays a real tag.
			return tmpParts.map((pPart) => (pPart.tag == null) ?
				this._nameHTML(pPart.name) :
				this._compoundDisplayHTML(pPart.tag, this._nameHTML(pPart.name), tmpMulti, tmpTagLast)).join(', ');
		}
		return tmpParts.map((pPart) => this._nameHTML(pPart.name)).join(', ');
	}

	/**
	 * The name an entity record with no display name paints: `<entity label> #<record ID>` (`Document #282`),
	 * the same label the Edit picker gives it. The record's own ID column wins over pFallbackID; '' when
	 * neither is set, which leaves _nameHTML's faded marker.
	 * @param {object} pPictForm @param {Record<string, any>|undefined} pRecord @param {any} pFallbackID
	 * @param {string} [pFieldLabel] - the field's label, which names an Organization's role.
	 * @return {string}
	 */
	_unnamedLabel(pPictForm, pRecord, pFallbackID, pFieldLabel)
	{
		const tmpRecordID = pRecord ? pRecord[`ID${pPictForm.Entity}`] : undefined;
		const tmpID = (tmpRecordID === undefined || tmpRecordID === null || tmpRecordID === '') ? pFallbackID : tmpRecordID;
		return libEntityDisplayName.unnamedEntityLabel(pPictForm.Entity, tmpID, pFieldLabel);
	}

	/**
	 * A display name, substituting a faded "no text provided" marker for a blank/missing value so a
	 * user/developer can spot bad data (a static option with no text, or an entity name with no ID to label
	 * it by) rather than an invisible gap. Non-blank names pass through unchanged.
	 * @param {any} pName @return {string}
	 */
	_nameHTML(pName)
	{
		if (pName === undefined || pName === null || String(pName).trim() === '')
		{
			return '<span class="picker-display-empty">no text provided</span>';
		}
		return String(pName);
	}

	/**
	 * Resolve each stored value to `{ name, tag }`. Entity fields resolve System-1-first: the persisted
	 * `__Record` snapshot whose identity matches the value (covers created-but-unsaved records, which no
	 * fetch can serve, and costs nothing for saved forms); then a NON-simple identity (GUID/Hash — the
	 * cascade family) fetches the legacy `FBL~<identity>~INN` shape through the broker; a plain numeric
	 * identity rides pict-section-picker's `createEntityResolveValue` (the original path). Static fields
	 * resolve their option label (tag null).
	 * @param {object} pInput @param {any} pValue @param {object} [pView] @param {number|string|null} [pRowIndex]
	 * @return {Promise<Array<{name:string, tag:any}>>}
	 */
	async _resolveParts(pInput, pValue, pView, pRowIndex)
	{
		const tmpPictForm = this._resolvedPictForm(pInput);
		const tmpValues = this._valueParts(tmpPictForm, pValue);
		if (tmpValues.length < 1)
		{
			return [];
		}
		// Unset sentinels (a pristine Number descriptor marshals in as manyfest's DataType default 0)
		// resolve to nothing, exactly as the edit picker paints its placeholder for them — there is no
		// entity #0 to fetch, and a bare "0" in a PDF reads as a bug. Static option lists keep the
		// widget's escape hatch below: a sentinel that IS a real option is a genuine selection.
		const tmpIsSentinel = (pValuePart) => libPickerEmptyValues.isEmptyValue(pInput, pValuePart);
		if (tmpPictForm.Entity)
		{
			const tmpEntityValues = tmpValues.filter((pValuePart) => !tmpIsSentinel(pValuePart));
			if (tmpEntityValues.length < 1)
			{
				return [];
			}
			const tmpIdentity = this._identityField(tmpPictForm);
			const tmpLocalRecords = this._localEntityRecords(pView, pInput, pRowIndex == null ? null : pRowIndex);
			const tmpResolver = (tmpIdentity === `ID${tmpPictForm.Entity}`) ? this._entityResolver(tmpPictForm) : null;
			const tmpFieldLabel = pInput && pInput.Name;
			return Promise.all(tmpEntityValues.map((pValuePart) =>
			{
				const tmpLocal = tmpLocalRecords.find((pRecord) => pRecord[tmpIdentity] == pValuePart);
				if (tmpLocal)
				{
					return Promise.resolve(this._recordParts(tmpLocal, tmpPictForm, tmpFieldLabel));
				}
				if (!tmpResolver)
				{
					return this._resolveEntityByIdentity(tmpPictForm, tmpIdentity, pValuePart, tmpFieldLabel);
				}
				return this._resolveEntity(tmpResolver, tmpPictForm, pValuePart, tmpFieldLabel);
			}));
		}
		const tmpOptions = await this._resolveStaticOptions(tmpPictForm);
		return tmpValues
			.filter((pValuePart) => !tmpIsSentinel(pValuePart) || tmpOptions.some((pOption) => String(pOption.id) === String(pValuePart)))
			.map((pValuePart) => ({ name: this._resolveOptionLabel(tmpOptions, pValuePart), tag: null }));
	}

	/**
	 * value → `{ name, tag }` by a NON-simple identity (GUID/Hash), through the broker's legacy-shape
	 * fetch (`FBL~<identity>~INN`) — getEntity/createEntityResolveValue can only look up by numeric id.
	 * No match (e.g. an unsaved GUID with no local snapshot) degrades to the raw value, like the id path.
	 * @param {object} pPictForm @param {string} pIdentityField @param {any} pValue @param {string} [pFieldLabel]
	 * @return {Promise<{name:string, tag:any}>}
	 */
	async _resolveEntityByIdentity(pPictForm, pIdentityField, pValue, pFieldLabel)
	{
		const tmpBroker = this.pict.providers.Picker;
		if (!tmpBroker || typeof tmpBroker._resolveEntityRecordByIdentity !== 'function')
		{
			this.pict.log.warn('PickerDisplay: the Picker broker is not registered; cannot resolve by identity.');
			return { name: String(pValue), tag: null };
		}
		const tmpRecord = await tmpBroker._resolveEntityRecordByIdentity(pPictForm.Entity, pIdentityField, pValue);
		if (!tmpRecord)
		{
			return { name: String(pValue), tag: null };
		}
		return this._recordParts(tmpRecord, pPictForm, pFieldLabel);
	}

	/**
	 * Build a pict-section-picker entity `ResolveValue(value) => Promise<{Text, Tag, ...}>` from the
	 * descriptor vocab (IdentityField → ValueField, EntityDisplayField → TextField, Join* / EntityTag
	 * passed through). Replaces the Select2Util.getEntityAsync resolve. Null when the picker provider
	 * isn't registered.
	 * @param {object} pPictForm @return {((pValue:any) => Promise<any>)|null}
	 */
	_entityResolver(pPictForm)
	{
		const tmpProvider = this.pict.providers['Pict-Section-Picker'];
		if (!tmpProvider || typeof tmpProvider.createEntityResolveValue !== 'function')
		{
			this.pict.log.warn('PickerDisplay: the Pict-Section-Picker provider is not registered; cannot resolve entity display names.');
			return null;
		}
		return tmpProvider.createEntityResolveValue(
		{
			Entity: pPictForm.Entity,
			ValueField: pPictForm.ValueField || pPictForm.IdentityField,
			TextField: pPictForm.TextField || pPictForm.EntityDisplayField,
			TextTemplate: pPictForm.TextTemplate,
			JoinEntity: pPictForm.JoinEntity,
			JoinField: pPictForm.JoinField,
			JoinEntityValueField: pPictForm.JoinEntityValueField,
			JoinEntityDisplayField: pPictForm.JoinEntityDisplayField,
			JoinEntityFirst: pPictForm.JoinEntityFirst,
			JoinSeparator: pPictForm.JoinSeparator,
			EntityTag: pPictForm.EntityTag,
			EntityTags: pPictForm.EntityTags,
		});
	}

	/**
	 * Whether the picker's own flat `pRecord[TextField]` provably cannot produce this descriptor's label,
	 * so the display has to resolve the name locally: a DOTTED display field, or a `User` with none
	 * configured (that table has no `Name` column, which is what the picker defaults to). Narrow on
	 * purpose — a TextTemplate or a JoinEntity compound is the picker's to compose, and recomputing
	 * locally would drop it.
	 * @param {object} pPictForm @return {boolean}
	 */
	_needsLocalNameResolution(pPictForm)
	{
		if (pPictForm.TextTemplate || pPictForm.JoinEntity)
		{
			return false;
		}
		const tmpDisplayField = pPictForm.TextField || pPictForm.EntityDisplayField;
		if (tmpDisplayField)
		{
			return tmpDisplayField.includes('.');
		}
		return pPictForm.Entity === 'User';
	}

	/**
	 * value (id) → `{ name, tag }` via the picker's ResolveValue. Missing entity, a failed read or a resolve
	 * error → the raw value as the name with no tag (never throws — the display degrades gracefully).
	 * @param {((pValue:any) => Promise<any>)|null} pResolver @param {object} pPictForm @param {any} pValue
	 * @param {string} [pFieldLabel]
	 * @return {Promise<{name:string, tag:any}>}
	 */
	async _resolveEntity(pResolver, pPictForm, pValue, pFieldLabel)
	{
		try
		{
			const tmpResolved = pResolver ? await pResolver(pValue) : null;
			if (!tmpResolved || (tmpResolved.Record && !libSharedEntityRead.isEntityRecord(pPictForm.Entity, tmpResolved.Record)))
			{
				return { name: String(pValue), tag: null };
			}
			// The picker composes its Text with a FLAT `pRecord[TextField]` (defaulting to 'Name'), which
			// cannot serve a dotted display field or the Name-less User table — so for those two shapes the
			// label is recomputed here off the record the resolve carries back. Same helper as the __Record
			// path, so all three entity paths in this provider agree.
			if (tmpResolved.Record && this._needsLocalNameResolution(pPictForm))
			{
				return this._recordParts(tmpResolved.Record, pPictForm, pFieldLabel);
			}
			const tmpName = (tmpResolved.Text !== undefined && tmpResolved.Text !== null && String(tmpResolved.Text).trim() !== '') ?
				tmpResolved.Text :
				this._unnamedLabel(pPictForm, tmpResolved.Record, pValue, pFieldLabel);
			const tmpTag = (tmpResolved.Tag !== undefined && tmpResolved.Tag !== null && tmpResolved.Tag !== '') ? tmpResolved.Tag : null;
			return { name: tmpName, tag: tmpTag };
		}
		catch (pError)
		{
			this.pict.log.warn(`PickerDisplay: could not resolve ${pPictForm.Entity} #${pValue} for display.`, pError);
			return { name: String(pValue), tag: null };
		}
	}

	/**
	 * The compound `[tag] name` markup for one entity value — output-identical to Select2Util's
	 * buildSelect2CompoundDisplayTemplate (styled by bulma-print.scss in Read + PDF), but built here so the
	 * provider carries no Select2Util code dependency. See the class NOTE re: the §9 class-name rename.
	 * @param {any} pTag @param {any} pName @param {boolean} pMultiple @param {boolean} pTagAtEnd @return {string}
	 */
	_compoundDisplayHTML(pTag, pName, pMultiple, pTagAtEnd)
	{
		const tmpName = `<span class="select2-entity-name">${pName === undefined || pName === null ? '' : pName}</span>`;
		const tmpTag = `<span class="tag select2-entity-tag">${pTag}</span>`;
		const tmpInner = pTagAtEnd ? `${tmpName}${tmpTag}` : `${tmpTag}${tmpName}`;
		return `<span class="select2-${pMultiple ? 'multiple' : 'single'}-entity-selection">${tmpInner}</span>`;
	}

	/**
	 * The option set to label a non-entity value with: a managed vocabulary's rows when the field is
	 * backed by one (falling back to its static list for a customer with no rows), else the static set.
	 * @param {object} pPictForm @return {Promise<Array<{id:any, text:string}>>}
	 */
	async _resolveStaticOptions(pPictForm)
	{
		if (pPictForm.SelectOptionsEntity)
		{
			return libEntityOptionSource.resolveOptionsAsync(this, { PictForm: pPictForm });
		}
		return this._staticOptions(pPictForm);
	}

	/**
	 * The static option set for a non-entity field: the inline `SelectOptions`, or a resolved
	 * `SelectOptionsPickList` (mirrors PictProvider-InputType-Select.handlePDFDisplay).
	 * @param {object} pPictForm @return {Array<{id:any, text:string}>}
	 */
	_staticOptions(pPictForm)
	{
		let tmpData = Array.isArray(pPictForm.SelectOptions) ? pPictForm.SelectOptions : null;
		const tmpMetaLists = this.pict.providers.DynamicMetaLists;
		if ((!tmpData || tmpData.length < 1) && pPictForm.SelectOptionsPickList && tmpMetaLists &&
			typeof tmpMetaLists.hasList === 'function' && tmpMetaLists.hasList(pPictForm.SelectOptionsPickList))
		{
			tmpData = tmpMetaLists.getList(pPictForm.SelectOptionsPickList);
		}
		return tmpData || [];
	}

	/** value → the matching option's text; falls back to the raw value when it isn't in the set. */
	_resolveOptionLabel(pOptions, pValue)
	{
		const tmpMatch = pOptions.find((pOption) => String(pOption.id) === String(pValue));
		return tmpMatch ? tmpMatch.text : String(pValue);
	}
}

module.exports = PickerDisplayProvider;
